import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { CMSBrand } from "../../../../services/cms";
import ImageUpload from "../../../../components/ImageUpload";

interface BrandsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingBrand: CMSBrand | null;
  setEditingBrand: React.Dispatch<React.SetStateAction<CMSBrand | null>>;
}

const BrandsDialog: React.FC<BrandsDialogProps> = ({
  open,
  onClose,
  onSave,
  editingBrand,
  setEditingBrand,
}) => {
  const { t } = useTranslation();

  const handleChange = (field: keyof CMSBrand, value: any) => {
    if (!editingBrand) return;
    setEditingBrand({
      ...editingBrand,
      [field]: value,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "95%", sm: "90%", md: "80%" },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
        {editingBrand?.id
          ? t("admin.contentManagement.editBrand")
          : t("admin.contentManagement.addBrand")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label={t("admin.contentManagement.fields.brandName")}
            fullWidth
            value={editingBrand?.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
          />
          <ImageUpload
            currentImage={editingBrand?.logo_url || ""}
            onImageChange={(url: string) => handleChange("logo_url", url)}
            folder="brands"
            label={t("admin.contentManagement.fields.brandLogo")}
          />
          <TextField
            label={t("admin.contentManagement.fields.websiteUrl")}
            fullWidth
            value={editingBrand?.website_url || ""}
            onChange={(e) => handleChange("website_url", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.description")}
            fullWidth
            multiline
            rows={2}
            value={editingBrand?.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.displayOrder")}
            fullWidth
            type="number"
            value={editingBrand?.display_order || 0}
            onChange={(e) => handleChange("display_order", parseInt(e.target.value))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editingBrand?.is_active ?? true}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
            }
            label={t("admin.contentManagement.active")}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>
          {t("admin.contentManagement.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          sx={{
            background: "linear-gradient(135deg, #00ff88 0%, #00cc6f 100%)",
          }}
        >
          {t("admin.contentManagement.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrandsDialog;